/**
 * Bruce — Alfred's chat assistant widget (bottom-right, every authenticated
 * page). Variant C of the approved mockups: the INPUT itself lives in the
 * corner. Three sizes:
 *   pill   — collapsed "Ask Bruce" pill (default; stays out of wide registers)
 *   open   — command bar + a compact thread above it
 *   drawer — full-height right panel for table-heavy answers
 * Whether it renders AT ALL is decided server-side (fetchBruceConfig).
 */

import {
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CloseIcon from '@material-ui/icons/Close'
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import SendIcon from '@material-ui/icons/Send'
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined'
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  fetchBruceConfig,
  fetchBruceHistory,
  fetchBruceReminders,
  sendBruceFeedback,
  sendBruceMessage,
  startBruceChat
} from 'api'
import { isUserAuthenticated } from 'app/helpers'
import { radius, shadow, space } from 'app/theme/tokens'

import BruceAgentsPanel from './BruceAgentsPanel'
import { ActionCards, RemindersPanel } from './BruceReminders'

const NAVY = '#282E3C'
const ORANGE = '#F25E35'
const CREAM = '#FBF5ED'
// Above the nav drawer (1100), below modals (1200) — see app/theme/tokens.js.
const WIDGET_Z = 1150

// How a turn ended, as the chat API reports it. On these two Bruce could not
// answer — the only place the full subject list is worth offering.
const UNANSWERED = ['no_agent', 'declined']
// Not an answer: a reminder the person set, posted into the chat when it fell
// due. Shown as its card alone — no subject chips, no thumbs.
const POSTED_REMINDER = 'reminder'
// How often the widget asks whether a reminder has fallen due.
const DUE_POLL_MS = 60000

const MODE_KEY = 'bruce.mode'
// HQ can look at Bruce exactly as a centre team does. Per browser, never sent
// to the server — it hides an HQ affordance, it does not change permissions,
// and the answers were already scoped to this user either way.
const PREVIEW_KEY = 'bruce.previewAsUser'

// How numbers are counted: Alfred's rules or Helios's (oh_assistant
// services/helios_basis.py). Per browser, remembered until changed; the
// server stores the basis on every answer so the two can be compared.
const BASIS_KEY = 'bruce.basis'
const HELIOS = 'helios'
const ALFRED = 'alfred'
const BASIS_TIP =
  "How Bruce counts enrolments and attendance. Alfred: what is active or upcoming today. Helios: the way Helios's cards count, every enrolment that ran at any point in the month except terminated. Both count Alfred's own records. Applies from your next question."

const readSavedBasis = () => {
  try {
    return window.localStorage.getItem(BASIS_KEY) === HELIOS ? HELIOS : ALFRED
  } catch (error) {
    return ALFRED
  }
}

const readSavedPreview = () => {
  try {
    return window.localStorage.getItem(PREVIEW_KEY) === '1'
  } catch (error) {
    return false
  }
}

const readSavedMode = () => {
  try {
    const saved = window.localStorage.getItem(MODE_KEY)
    return saved === 'open' || saved === 'drawer' ? saved : 'pill'
  } catch (error) {
    return 'pill'
  }
}

const saveMode = (mode) => {
  try {
    window.localStorage.setItem(MODE_KEY, mode)
  } catch (error) {
    // localStorage unavailable — the widget just forgets its size
  }
}

const URL_RE = /(https?:\/\/[^\s<>"']+)/g
// A count that carries a unit is the thing a centre acts on — "8 overdue",
// "198 days". Bolding those, and nothing else, keeps the emphasis meaningful.
const COUNT_RE =
  /\b(\d[\d,]*\s?(?:days?|overdue|pending|upcoming|visitors?|parents?|families|items?|centres?|orders?|coins?|%))/gi
const PHONE_RE = /\b(\d{10})\b/g

const escapeForRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Names come from what the tools returned, never from parsing Bruce's text, so
// a link can only ever point at someone the answer really is about. Longest
// first, so "Neha Nagaraja" wins over a bare "Neha".
const namePattern = (refs) => {
  const names = Object.keys(refs || {}).sort((a, b) => b.length - a.length)
  if (!names.length) return null
  return new RegExp(`(${names.map(escapeForRe).join('|')})`, 'g')
}

const anchor = (key, href, text, external) => (
  <a
    key={key}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      color: ORANGE,
      fontWeight: external ? 400 : 600,
      wordBreak: external ? 'break-all' : 'normal'
    }}
  >
    {text}
  </a>
)

// One pass per decoration, each splitting the parts the previous one left as
// plain strings. Order matters: URLs first (they may contain digits), then
// names, then counts, then phone numbers.
const decorate = (text, refs, keyBase) => {
  let parts = [String(text || '')]

  const split = (re, render) => {
    const next = []
    parts.forEach((part, pi) => {
      if (typeof part !== 'string') {
        next.push(part)
        return
      }
      part.split(re).forEach((bit, bi) => {
        if (!bit) return
        re.lastIndex = 0
        next.push(re.test(bit) ? render(bit, `${keyBase}-${pi}-${bi}`) : bit)
        re.lastIndex = 0
      })
    })
    parts = next
  }

  split(URL_RE, (bit, key) => anchor(key, bit, bit, true))
  const names = namePattern(refs)
  if (names) {
    split(names, (bit, key) => anchor(key, refs[bit], bit, false))
  }
  split(COUNT_RE, (bit, key) => (
    <strong key={key} style={{ fontWeight: 600, color: '#282E3C' }}>
      {bit}
    </strong>
  ))
  split(PHONE_RE, (bit, key) => (
    <span key={key} style={{ color: '#9A8F7C' }}>
      {bit}
    </span>
  ))
  return parts
}

// Bruce writes plain text. This renders it: the opening line as the answer,
// dashed lines as a scannable list, and inside every line the names, counts and
// numbers a centre actually reads.
const renderAnswer = (text, refs, classes) => {
  const lines = String(text || '').split('\n')
  let seenBody = false
  return lines.map((line, i) => {
    const key = `l${i}`
    const trimmed = line.trim()
    if (!trimmed) return <div key={key} className={classes.answerGap} />
    const bullet = /^[-•]\s+/.test(trimmed)
    if (bullet) {
      seenBody = true
      return (
        <div key={key} className={classes.answerRow}>
          {decorate(trimmed.replace(/^[-•]\s+/, ''), refs, key)}
        </div>
      )
    }
    const lead = !seenBody
    seenBody = true
    return (
      <div key={key} className={lead ? classes.answerLead : undefined}>
        {decorate(trimmed, refs, key)}
      </div>
    )
  })
}

const agentTopic = (agents, key) => {
  const found = agents.find((a) => a.key === key)
  if (!found) return key
  const topic = found.topic || found.label || key
  return topic.charAt(0).toUpperCase() + topic.slice(1)
}

const topicOf = (agent) => {
  const topic = agent.topic || agent.label || agent.key
  return topic.charAt(0).toUpperCase() + topic.slice(1)
}

// The subjects, as buttons. Bruce used to read all nine out inside the answer
// itself; nobody reads a nine-item sentence (Satya, 09-09: "so many pointers
// ... people will get confused"). Tapping one puts that card's OWN sample
// question in the box, so the next step is a real question, not more prose.
const TopicChips = ({ agents, onPick, classes }) =>
  agents.length ? (
    <div className={classes.chipRow}>
      {agents.map((a) => (
        <button
          key={a.key}
          type="button"
          className={classes.topicChip}
          onClick={() =>
            onPick(a.example || `Tell me about ${a.topic || a.label}`)
          }
        >
          {topicOf(a)}
        </button>
      ))}
    </div>
  ) : null

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    right: space.lg,
    bottom: space.lg,
    zIndex: WIDGET_Z,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: space.sm,
    fontFamily: theme.typography.fontFamily
  },
  pill: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: space.sm,
    background: NAVY,
    color: '#FFF',
    border: 'none',
    borderRadius: radius.pill,
    padding: `${space.sm} ${space.md}`,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: shadow.lg,
    '&:hover': { background: '#343B4C' }
  },
  dueDot: {
    position: 'absolute',
    top: -3,
    right: -3,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: ORANGE,
    border: `2px solid ${CREAM}`
  },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: ORANGE,
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: space.sm,
    background: NAVY,
    borderRadius: radius.pill,
    padding: `6px 6px 6px ${space.md}`,
    width: 320,
    boxShadow: shadow.lg
  },
  barInput: {
    flex: 1,
    color: '#F5F1E9',
    fontSize: 13
  },
  sendBtn: {
    background: ORANGE,
    color: '#FFF',
    width: 32,
    height: 32,
    '&:hover': { background: '#DE4F28' },
    '&.Mui-disabled': { background: '#4A5162', color: '#8B92A3' }
  },
  thread: {
    width: 344,
    maxHeight: 420,
    background: '#FFF',
    border: '1px solid #E8DFD2',
    borderRadius: radius.lg,
    boxShadow: shadow.modal,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 400,
    maxWidth: '100vw',
    background: '#FFF',
    borderLeft: '1px solid #E8DFD2',
    boxShadow: shadow.modal,
    zIndex: WIDGET_Z,
    display: 'flex',
    flexDirection: 'column'
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: space.sm,
    padding: `${space.sm} ${space.md}`,
    background: NAVY,
    color: '#FFF',
    flexShrink: 0
  },
  headTitle: { fontSize: 14, fontWeight: 800, lineHeight: 1.1 },
  headSub: { fontSize: 10, color: '#AEB4C2' },
  headBtn: { color: '#8B92A3', padding: 4 },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: space.md,
    display: 'flex',
    flexDirection: 'column',
    gap: space.sm,
    background: '#FBF9F4'
  },
  msgUser: {
    alignSelf: 'flex-end',
    maxWidth: '85%',
    background: NAVY,
    color: '#F5F1E9',
    borderRadius: '12px 12px 4px 12px',
    padding: `${space.sm} 12px`,
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  msgBot: {
    alignSelf: 'flex-start',
    maxWidth: '92%',
    background: '#FFF',
    color: NAVY,
    border: '1px solid #EDE5D5',
    borderRadius: '12px 12px 12px 4px',
    padding: `${space.sm} 12px`,
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  chip: {
    display: 'inline-block',
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: ORANGE,
    background: '#FDEFEA',
    border: '1px solid #F8CDBE',
    borderRadius: radius.pill,
    padding: '1px 8px',
    marginBottom: 4,
    marginRight: 4
  },
  heliosChip: {
    color: '#2D5E93',
    background: '#E9F1FB',
    border: '1px solid #BFD5EE'
  },
  basisSwitch: {
    display: 'flex',
    flexShrink: 0,
    background: '#3A4152',
    borderRadius: radius.pill,
    padding: 2,
    marginRight: space.xs
  },
  basisOption: {
    border: 'none',
    background: 'transparent',
    color: '#C9CEDA',
    borderRadius: radius.pill,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { color: '#FFF' }
  },
  basisOptionAlfred: {
    background: '#FFF',
    color: NAVY,
    fontWeight: 700,
    '&:hover': { color: NAVY }
  },
  basisOptionHelios: {
    background: '#E9F1FB',
    color: '#2D5E93',
    fontWeight: 700,
    '&:hover': { color: '#2D5E93' }
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 7
  },
  topicChip: {
    font: 'inherit',
    fontSize: 11.5,
    lineHeight: 1.3,
    padding: '4px 10px',
    borderRadius: radius.pill,
    border: '1px solid #E4DACA',
    background: '#FFF',
    color: '#4A4438',
    cursor: 'pointer',
    '&:hover': { borderColor: ORANGE, color: ORANGE }
  },
  moreLink: {
    display: 'inline-block',
    marginTop: 6,
    fontSize: 11.5,
    color: ORANGE,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  feedbackRow: {
    display: 'flex',
    gap: 2,
    marginTop: 2
  },
  feedbackBtn: {
    padding: 2,
    color: '#B4AA95',
    '& svg': { fontSize: 14 }
  },
  feedbackActive: { color: ORANGE },
  typing: {
    alignSelf: 'flex-start',
    color: '#9A8F7C',
    fontSize: 12,
    padding: `${space.xs} ${space.sm}`
  },
  scopeNote: {
    fontSize: 9.5,
    color: '#9A8F7C',
    textAlign: 'center',
    padding: `${space.xs} ${space.sm} ${space.sm}`,
    background: '#FFF',
    flexShrink: 0
  },
  answerLead: {
    fontWeight: 600,
    color: '#282E3C',
    marginBottom: 2
  },
  answerRow: {
    paddingLeft: 10,
    position: 'relative',
    marginBottom: 3,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '0.62em',
      width: 3,
      height: 3,
      borderRadius: '50%',
      background: '#C9BFAE'
    }
  },
  answerGap: {
    height: 6
  },
  scopeLink: {
    color: '#F25E35',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  drawerInputRow: {
    display: 'flex',
    gap: space.sm,
    padding: space.sm,
    borderTop: '1px solid #EDE5D5',
    background: '#FFF',
    flexShrink: 0
  },
  drawerInput: {
    flex: 1,
    background: CREAM,
    border: '1px solid #E4DCCB',
    borderRadius: radius.md,
    padding: `6px ${space.sm}`,
    fontSize: 13,
    color: NAVY
  }
}))

const BruceWidget = ({ onConfig }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [enabled, setEnabled] = useState(false)
  const [mode, setMode] = useState(readSavedMode)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [view, setView] = useState('chat')
  const [agents, setAgents] = useState([])
  const [canSeeAgents, setCanSeeAgents] = useState(false)
  const [previewAsUser, setPreviewAsUser] = useState(readSavedPreview)
  const [canPickBasis, setCanPickBasis] = useState(false)
  // How-to desk: Bruce points to the screen and gives no numbers.
  const [howtoOnly, setHowtoOnly] = useState(false)
  const [basis, setBasis] = useState(readSavedBasis)
  // uuid of the unanswered message whose subject chips are open, if any.
  const [topicsFor, setTopicsFor] = useState('')
  const [remindersOn, setRemindersOn] = useState(false)
  // Reminders that fell due and still wait for Done: the dot on the pill.
  const [dueCount, setDueCount] = useState(0)
  const [menuAnchor, setMenuAnchor] = useState(null)

  const conversationRef = useRef(null)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const dueRef = useRef(0)
  const sendingRef = useRef(false)
  sendingRef.current = sending

  useEffect(() => {
    // The isUserAuthenticated() guard is load-bearing, for the same reason it is
    // on loadMyAccess in MainLayout: this widget is mounted unconditionally by
    // that shell, and the shell ALSO mounts for a logged-out user. Calling
    // /assistant/config without a token 401s, and request.js turns any 401 into
    // a global LOGOUT_START -> logout + reload -> the widget mounts again and
    // asks again. That is an infinite refresh loop on the login page, and the
    // .catch() below cannot stop it: the interceptor has already dispatched by
    // the time this promise rejects. Observed on dev 2026-09-08 — 87 requests in
    // five seconds, the login page reloading over itself.
    if (!isUserAuthenticated()) return undefined
    let alive = true
    fetchBruceConfig()
      .then((data) => {
        if (!alive) return
        if (onConfig) onConfig(data)
        if (!data || !data.enabled) return
        setEnabled(true)
        setAgents(data.agents || [])
        // Owner only once an owner is set (Satya, 2026-09-19).
        setCanSeeAgents(Boolean(data.can_see_agents))
        // HQ only while the two counts are compared (Satya, 2026-09-15).
        setCanPickBasis(
          Boolean(
            'can_pick_basis' in data ? data.can_pick_basis : data.can_see_agents
          ) && (data.bases || []).some((b) => b.key === HELIOS)
        )
        setRemindersOn(Boolean(data.reminders))
        setHowtoOnly(Boolean(data.howto_only))
      })
      .catch(() => {
        // Not enabled for this user / endpoint absent — widget stays hidden.
      })
    return () => {
      alive = false
    }
  }, [onConfig])

  useEffect(() => {
    if (!enabled || historyLoaded || mode === 'pill') return
    setHistoryLoaded(true)
    fetchBruceHistory()
      .then((data) => {
        if (!data || !data.conversation) return
        conversationRef.current = data.conversation
        setMessages(
          (data.messages || []).map((m) => ({
            uuid: m.uuid,
            role: m.role,
            content: m.content,
            agents: m.agents || [],
            refs: m.refs || {},
            resolution: m.resolution || '',
            basis: m.basis || ALFRED,
            actions: m.actions || [],
            feedback: m.feedback || null
          }))
        )
      })
      .catch(() => {})
  }, [enabled, historyLoaded, mode])

  // A reminder that falls due is posted into the chat on the server. Ask once a
  // minute how many wait for Done: that is the dot, and a new one reloads the
  // chat so its card is there when the window opens.
  useEffect(() => {
    if (!enabled || !remindersOn) return undefined
    let alive = true
    const check = () => {
      fetchBruceReminders()
        .then((data) => {
          if (!alive || !data || typeof data.due !== 'number') return
          if (data.due > dueRef.current && !sendingRef.current) {
            setHistoryLoaded(false)
          }
          dueRef.current = data.due
          setDueCount(data.due)
        })
        .catch(() => {})
    }
    check()
    const timer = window.setInterval(check, DUE_POLL_MS)
    return () => {
      alive = false
      window.clearInterval(timer)
    }
  }, [enabled, remindersOn])

  const onDueCount = useCallback((count) => {
    dueRef.current = count
    setDueCount(count)
  }, [])

  // A card changed state (saved, dismissed, done, snoozed): keep it on the
  // message so the chat shows it as it was left.
  const onActionChange = useCallback((messageUuid, next) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uuid === messageUuid
          ? {
              ...m,
              actions: (m.actions || []).map((a) =>
                a.id === next.id ? next : a
              )
            }
          : m
      )
    )
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, sending, mode])

  const switchMode = useCallback((next) => {
    setMode(next)
    saveMode(next)
    if (next !== 'drawer') setView('chat')
  }, [])

  // A centre-team preview never offers the choice, so it never sends it.
  const showBasis = canPickBasis && !previewAsUser
  const activeBasis = showBasis ? basis : ALFRED

  const pickBasis = useCallback((next) => {
    setBasis(next)
    try {
      window.localStorage.setItem(BASIS_KEY, next)
    } catch (error) {
      /* a browser that refuses storage keeps the choice for this session */
    }
  }, [])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    setMessages((prev) => [
      ...prev,
      { uuid: `local-${Date.now()}`, role: 'user', content: text, agents: [] }
    ])
    setSending(true)
    try {
      const data = await sendBruceMessage(
        text,
        conversationRef.current,
        activeBasis
      )
      conversationRef.current = data.conversation
      setMessages((prev) => [
        ...prev,
        {
          uuid: data.message,
          role: 'assistant',
          content: data.reply,
          agents: data.agents || [],
          refs: data.refs || {},
          resolution: data.resolution || '',
          basis: data.basis || activeBasis,
          actions: data.actions || [],
          feedback: null
        }
      ])
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          uuid: `err-${Date.now()}`,
          role: 'assistant',
          content: 'Something went wrong — please try again.',
          agents: [],
          feedback: null
        }
      ])
    } finally {
      setSending(false)
    }
  }, [input, sending, activeBasis])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
      if (e.key === 'Escape') switchMode('pill')
    },
    [handleSend, switchMode]
  )

  const showAgentsButton = canSeeAgents && !previewAsUser

  const setPreview = useCallback((on) => {
    setPreviewAsUser(on)
    if (on) setView('chat')
    try {
      window.localStorage.setItem(PREVIEW_KEY, on ? '1' : '0')
    } catch (error) {
      /* a browser that refuses storage still previews for this session */
    }
  }, [])

  const openReminders = useCallback(() => {
    setMenuAnchor(null)
    setView('reminders')
  }, [])

  // A clean chat: the old one is kept on the server, and reminders carry over.
  const newChat = useCallback(async () => {
    setMenuAnchor(null)
    try {
      const data = await startBruceChat()
      if (data && data.conversation) {
        conversationRef.current = data.conversation
        setMessages([])
        setTopicsFor('')
        setView('chat')
      }
    } catch (error) {
      /* the current chat stays as it is */
    }
  }, [])

  const openAgents = useCallback(() => {
    setView('agents')
    switchMode('drawer')
  }, [switchMode])

  const askFromAgents = useCallback((question) => {
    setView('chat')
    setInput(question)
    window.setTimeout(() => {
      if (inputRef.current) inputRef.current.focus()
    }, 0)
  }, [])

  const handleFeedback = useCallback(
    (uuid, verdict) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.uuid === uuid
            ? { ...m, feedback: m.feedback === verdict ? null : verdict }
            : m
        )
      )
      const current = messages.find((m) => m.uuid === uuid)
      const next = current && current.feedback === verdict ? '' : verdict
      sendBruceFeedback(uuid, next).catch(() => {})
    },
    [messages]
  )

  if (!enabled || isMobile) return null

  // At the top of the window, both choices in view (Satya, 2026-09-16: a
  // one-word pill by the question box was confusing — it read "Alfred" above
  // an answer tagged Helios, and did not say what the other option was).
  const basisSwitch = showBasis && view === 'chat' && (
    <Tooltip title={BASIS_TIP}>
      <div
        className={classes.basisSwitch}
        role="group"
        aria-label="Count the Alfred way or the Helios way"
      >
        {[
          [ALFRED, 'Alfred', classes.basisOptionAlfred],
          [HELIOS, 'Helios', classes.basisOptionHelios]
        ].map(([key, label, onClass]) => (
          <button
            key={key}
            type="button"
            className={`${classes.basisOption} ${basis === key ? onClass : ''}`}
            aria-pressed={basis === key}
            onClick={() => pickBasis(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </Tooltip>
  )

  const threadBody = (
    <>
      <div className={classes.scroll} ref={scrollRef}>
        {messages.length === 0 && !sending && (
          <div>
            <div className={classes.typing}>What would you like to know?</div>
            <TopicChips
              agents={agents}
              onPick={askFromAgents}
              classes={classes}
            />
          </div>
        )}
        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.uuid} className={classes.msgUser}>
              {m.content}
            </div>
          ) : m.resolution === POSTED_REMINDER ? (
            <div key={m.uuid} className={classes.msgBot}>
              <ActionCards
                actions={m.actions}
                messageUuid={m.uuid}
                onActionChange={onActionChange}
                onRetype={askFromAgents}
                onDueCount={onDueCount}
              />
            </div>
          ) : (
            <div key={m.uuid} className={classes.msgBot}>
              {(m.agents || []).map((a) => (
                <span key={a} className={classes.chip}>
                  {agentTopic(agents, a)}
                </span>
              ))}
              {m.basis === HELIOS && (
                <Tooltip title="Counted the way Helios counts: every enrolment that ran at any point in the month, except terminated">
                  <span className={`${classes.chip} ${classes.heliosChip}`}>
                    Helios count
                  </span>
                </Tooltip>
              )}
              {((m.agents && m.agents.length > 0) || m.basis === HELIOS) && (
                <br />
              )}
              {renderAnswer(m.content, m.refs, classes)}
              <ActionCards
                actions={m.actions}
                messageUuid={m.uuid}
                onActionChange={onActionChange}
                onRetype={askFromAgents}
                onDueCount={onDueCount}
              />
              {UNANSWERED.includes(m.resolution) &&
                (topicsFor === m.uuid ? (
                  <TopicChips
                    agents={agents}
                    onPick={askFromAgents}
                    classes={classes}
                  />
                ) : (
                  <div
                    className={classes.moreLink}
                    role="button"
                    tabIndex={0}
                    onClick={() => setTopicsFor(m.uuid)}
                    onKeyDown={(e) => e.key === 'Enter' && setTopicsFor(m.uuid)}
                  >
                    See everything I cover
                  </div>
                ))}
              {!String(m.uuid).startsWith('local') && (
                <div className={classes.feedbackRow}>
                  <IconButton
                    size="small"
                    className={`${classes.feedbackBtn} ${
                      m.feedback === 'up' ? classes.feedbackActive : ''
                    }`}
                    onClick={() => handleFeedback(m.uuid, 'up')}
                  >
                    <ThumbUpAltOutlinedIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={`${classes.feedbackBtn} ${
                      m.feedback === 'down' ? classes.feedbackActive : ''
                    }`}
                    onClick={() => handleFeedback(m.uuid, 'down')}
                  >
                    <ThumbDownAltOutlinedIcon />
                  </IconButton>
                </div>
              )}
            </div>
          )
        )}
        {sending && <div className={classes.typing}>Bruce is thinking…</div>}
      </div>
      <div className={classes.scopeNote}>
        {previewAsUser ? (
          <>
            You are seeing Bruce as a centre team does.{' '}
            <span
              className={classes.scopeLink}
              role="button"
              tabIndex={0}
              onClick={() => setPreview(false)}
              onKeyDown={(e) => e.key === 'Enter' && setPreview(false)}
            >
              Back to the HQ view
            </span>
          </>
        ) : showBasis && basis === HELIOS ? (
          'Helios count: every enrolment that ran in the month, except terminated'
        ) : howtoOnly ? (
          'Bruce shows you where to look in Alfred. It does not give numbers.'
        ) : (
          'Answers are scoped to what your Alfred role can see'
        )}
      </div>
    </>
  )

  const remindersBody = (
    <RemindersPanel onAsk={askFromAgents} onDueCount={onDueCount} />
  )

  const head = (expanded) => (
    <div className={classes.head}>
      <div className={classes.avatar}>B</div>
      <div>
        <div className={classes.headTitle}>Bruce</div>
        <div className={classes.headSub}>
          {view === 'reminders' ? 'My reminders' : "Alfred's assistant"}
        </div>
      </div>
      <div
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
      >
        {basisSwitch}
        {view !== 'chat' ? (
          <Tooltip title="Back to chat">
            <IconButton
              className={classes.headBtn}
              onClick={() => setView('chat')}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        ) : (
          showAgentsButton && (
            <Tooltip title="Agents — who answers what, and how well">
              <IconButton className={classes.headBtn} onClick={openAgents}>
                <DashboardOutlinedIcon />
              </IconButton>
            </Tooltip>
          )
        )}
        {view === 'chat' && (
          <Tooltip title="More: my reminders, new chat">
            <IconButton
              className={classes.headBtn}
              aria-label="More"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {remindersOn && (
            <MenuItem onClick={openReminders}>
              {dueCount > 0 ? `My reminders (${dueCount} due)` : 'My reminders'}
            </MenuItem>
          )}
          <MenuItem onClick={newChat}>New chat</MenuItem>
        </Menu>
        <Tooltip title={expanded ? 'Smaller' : 'Expand'}>
          <IconButton
            className={classes.headBtn}
            onClick={() => switchMode(expanded ? 'open' : 'drawer')}
          >
            {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Minimise">
          <IconButton
            className={classes.headBtn}
            onClick={() => switchMode('pill')}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  )

  if (mode === 'drawer' && view === 'agents') {
    return (
      <div className={classes.drawer}>
        {head(true)}
        <BruceAgentsPanel
          onAsk={askFromAgents}
          onPreviewAsUser={() => setPreview(true)}
        />
      </div>
    )
  }

  if (mode === 'drawer') {
    return (
      <div className={classes.drawer}>
        {head(true)}
        {view === 'reminders' ? remindersBody : threadBody}
        <div className={classes.drawerInputRow}>
          <InputBase
            className={classes.drawerInput}
            placeholder="Ask Bruce…"
            value={input}
            inputRef={inputRef}
            onChange={(e) => {
              const { value } = e.target
              setInput(value)
            }}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <IconButton
            className={classes.sendBtn}
            onClick={handleSend}
            disabled={sending || !input.trim()}
          >
            <SendIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {mode === 'open' && (
        <div
          className={classes.thread}
          style={showBasis ? { width: 400 } : undefined}
        >
          {head(false)}
          {view === 'reminders' ? remindersBody : threadBody}
        </div>
      )}
      {mode === 'pill' ? (
        <button
          type="button"
          className={classes.pill}
          onClick={() => switchMode('open')}
        >
          <span className={classes.avatar}>B</span>
          Ask Bruce
          {dueCount > 0 && (
            <span
              className={classes.dueDot}
              aria-label={`${dueCount} reminder${dueCount > 1 ? 's' : ''} due`}
            />
          )}
        </button>
      ) : (
        <div
          className={classes.bar}
          style={showBasis ? { width: 400 } : undefined}
        >
          <InputBase
            className={classes.barInput}
            placeholder="Ask Bruce…"
            value={input}
            inputRef={inputRef}
            onChange={(e) => {
              const { value } = e.target
              setInput(value)
            }}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <IconButton
            className={classes.sendBtn}
            onClick={handleSend}
            disabled={sending || !input.trim()}
          >
            <SendIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>
      )}
    </div>
  )
}

BruceWidget.propTypes = {
  // Hands the /assistant/config answer up to MainLayout (owner-only sidebar).
  onConfig: PropTypes.func
}

BruceWidget.defaultProps = {
  onConfig: null
}

export default BruceWidget
